import { Select, Tag } from 'antd';

import {
	PROCESS_LABELS,
	type ProcessKind,
	type ProcessSpecRef,
} from '../../interfaces/ProcessCatalog.Interface';
import { useGetProcessSpecsQuery } from '../../redux/api/processCatalog';

interface Props {
	kind: ProcessKind;
	/** Current choice. Passed as the nested ref the detail payloads carry. */
	value: ProcessSpecRef | null | undefined;
	onChange: (specId: string | null) => void;
	disabled?: boolean;
	loading?: boolean;
	allowClear?: boolean;
	width?: number;
	placeholder?: string;
}

/**
 * Picker over the active rows of one catalog kind.
 *
 * A row retired after a job was set keeps showing on that job — the parts are
 * still supposed to be cleaned that way — so the current value is added to the
 * options when the catalog no longer offers it. "(retired)" is only used when
 * we know the row is inactive or the catalog loaded without it; an empty or
 * failed list is not treated as retired.
 */
export default function ProcessSpecSelect({
	kind,
	value,
	onChange,
	disabled = false,
	loading = false,
	allowClear = false,
	width = 240,
	placeholder,
}: Props) {
	const { data, isLoading, isSuccess } = useGetProcessSpecsQuery({
		kind,
		is_active: true,
	});

	const specs = data ?? [];
	const options = specs.map((spec) => ({
		value: spec.id,
		label: spec.name,
		special: spec.is_special,
	}));

	if (value && !options.some((option) => option.value === value.id)) {
		// Reached only when the value is absent from the options, so a
		// successful load is itself the evidence that the row is gone. While
		// the request is in flight or has failed we know nothing, and an
		// absent row must not be labelled retired on a guess.
		const knownRetired = value.is_active === false || isSuccess;
		options.unshift({
			value: value.id,
			label: knownRetired ? `${value.name} (retired)` : value.name,
			special: value.is_special,
		});
	}

	return (
		<Select
			style={{ width }}
			value={value?.id ?? undefined}
			placeholder={placeholder ?? `Select ${PROCESS_LABELS[kind]}`}
			allowClear={allowClear}
			disabled={disabled}
			loading={isLoading || loading}
			onChange={(next) => onChange(next ?? null)}
			optionLabelProp="label"
		>
			{options.map((option) => (
				<Select.Option
					key={option.value}
					value={option.value}
					label={option.label}
				>
					<div className="flex items-center justify-between gap-2">
						<span>{option.label}</span>
						{option.special && (
							<Tag color="orange" style={{ margin: 0 }}>
								Special
							</Tag>
						)}
					</div>
				</Select.Option>
			))}
		</Select>
	);
}
